import { Patient } from '@medplum/fhirtypes';
import { BariatricProcedureStage, PATIENT_FILTER_TAG_SYSTEMS, Track } from './constants';
import { getMetaTag } from './meta-tags';
import { isPartnerTrack, readStageCode } from './pac-inquiry-list';

/** The Revenue ledger's Types (BAR-3030), in the summary's order. */
export const LEDGER_TYPES = [
  'Self-pay procedure',
  'Self-pay subscription',
  'Employer benefit revenue',
  'Insurance coverage revenue',
  'Refund',
  'Customer service fee',
] as const;

export type LedgerType = (typeof LEDGER_TYPES)[number];

/** Ledger sources in the summary's order: the payments-DB processors, then the receivable payors. */
export const LEDGER_SOURCES = [
  'STRIPE',
  'CHERRY',
  'CARECREDIT',
  'PATIENTFI',
  'MANUAL',
  'CARRUM',
  'TRANSCARENT',
  'HOSPITAL',
] as const;

export type LedgerSource = (typeof LEDGER_SOURCES)[number];

export const LEDGER_SOURCE_LABEL: Record<LedgerSource, string> = {
  STRIPE: 'Stripe',
  CHERRY: 'Cherry',
  CARECREDIT: 'CareCredit',
  PATIENTFI: 'PatientFi',
  MANUAL: 'Manual',
  CARRUM: 'Carrum',
  TRANSCARENT: 'Transcarent',
  HOSPITAL: 'Hospital',
};

/** The track `patient-filter-tags` stamps on the Patient; undefined before the bot has run. */
export function readPatientTrack(patient: Patient): Track | undefined {
  return getMetaTag(patient, PATIENT_FILTER_TAG_SYSTEMS.track)?.code as Track | undefined;
}

export interface LedgerTypeInput {
  kind: 'payment' | 'refund';
  transactionId: string | null;
  source: string | null;
  /** For a refund, the type of the request it settles (BAR-3033). */
  refundRequestType?: 'refund' | 'customer_service_fee' | null;
}

/**
 * A ledger row's Type, as the spec's table derives it.
 *
 * Money back is a Refund unless it settles a request that keeps the procedure
 * (Customer service fee, BAR-3033). Money in follows the patient: a verified
 * partner track is employer revenue, insurance is insurance revenue, and a
 * self-pay patient on a subscription is subscription revenue. With no patient,
 * the payor decides, and a Stripe invoice (`in_`) is a subscription.
 *
 * `Track.EMPLOYER` counts as self-pay: that benefit is self-reported and the
 * patient is paying, not an employer.
 */
export function ledgerType(row: LedgerTypeInput, patient: Patient | undefined): LedgerType {
  if (row.kind === 'refund') {
    return row.refundRequestType === 'customer_service_fee' ? 'Customer service fee' : 'Refund';
  }

  const track = patient ? readPatientTrack(patient) : undefined;
  if (isPartnerTrack(track) || row.source === 'CARRUM' || row.source === 'TRANSCARENT') {
    return 'Employer benefit revenue';
  }
  if (track === Track.INSURANCE || row.source === 'HOSPITAL') {
    return 'Insurance coverage revenue';
  }

  const isSubscription = patient
    ? readStageCode(patient) === BariatricProcedureStage.SUBSCRIPTION_ACTIVE
    : Boolean(row.transactionId?.startsWith('in_'));
  return isSubscription ? 'Self-pay subscription' : 'Self-pay procedure';
}
